import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaWallet,
  FaCoins,
  FaShieldAlt as FaShield,
  FaHistory,
  FaArrowDown,
  FaArrowLeft,
  FaArrowRight,
  FaArrowUp,
  FaLock,
  FaExchangeAlt,
  FaCheckCircle,
  FaCopy,
  FaUpload,
  FaHeadset,
  FaTelegramPlane,
  FaWhatsapp,
  FaEnvelope,
  FaSignOutAlt
} from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { useBalance } from '../context/BalanceContext';
import { useNotification } from '../contexts/NotificationContext';
import { usePrices } from '../context/PriceContext';
import { doc, getDoc, updateDoc, serverTimestamp, setDoc, onSnapshot, collection, query, where } from 'firebase/firestore';
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword, sendPasswordResetEmail } from 'firebase/auth';
import { db, auth } from '../firebase/config';
import { logoutUser } from '../firebase/authService';
import CoinSelectorControl from '../components/ui/CoinSelectorControl';
import { listenToDefaultAddresses } from '../services/walletService';
import { QRCodeSVG } from 'qrcode.react';
import { useTawkTo } from '../hooks/useTawkTo';

const DEFAULT_COIN_LIST = ['USDT', 'USDC', 'BTC', 'ETH', 'XRP', 'SOL', 'BNB', 'ADA'];
const TRADING_COINS = ['USDT', 'USDC'];

const DEFAULT_WALLET_ADDRESSES = {};

const DEFAULT_SUPPORT_CONTACTS = {
  whatsapp: 'https://wa.me/1234567890',
  telegram: 'https://t.me/GeminiAITradingSupport'
};

const BALANCE_PANEL_ORDER = ['funding', 'transfer', 'trading', 'arbitrage', 'deposit', 'withdraw'];

const PANEL_CONFIG = {
  funding: { label: 'Funding', icon: FaWallet },
  transfer: { label: 'Transfer', icon: FaExchangeAlt },
  trading: { label: 'Trading', icon: FaCoins },
  arbitrage: { label: 'Arbitrage', icon: FaCoins },
  deposit: { label: 'Deposit', icon: FaArrowDown },
  withdraw: { label: 'Withdraw', icon: FaArrowUp }
};

const PRIMARY_SECTIONS = [
  {
    key: 'balances',
    label: 'Balances',
    description: 'Track assets across trading, funding, and arbitrage accounts.',
    icon: FaCoins,
    gradient: 'from-blue-500 to-cyan-500'
  },
  {
    key: 'transactions',
    label: 'Transactions',
    description: 'Audit deposits, withdrawals, transfers, and system adjustments.',
    icon: FaHistory,
    gradient: 'from-purple-500 to-pink-500'
  },
  {
    key: 'security',
    label: 'Security',
    description: 'Manage KYC documents, 2FA methods, and real-time alerting.',
    icon: FaShield,
    gradient: 'from-emerald-500 to-teal-500'
  }
];

const SECURITY_SECTIONS = [
  {
    key: 'verification',
    label: 'Account Verification',
    description: 'Submit documents and monitor review status.',
    icon: FaCheckCircle,
    gradient: 'from-emerald-500 to-teal-500'
  },
  {
    key: 'contacts',
    label: 'Trusted Contact Channels',
    description: 'Configure Telegram or WhatsApp alerts for security events.',
    icon: FaTelegramPlane,
    gradient: 'from-sky-500 to-cyan-500'
  },
  {
    key: 'support',
    label: 'Customer Service',
    description: 'Chat with Gemini AI support through Telegram or WhatsApp.',
    icon: FaHeadset,
    gradient: 'from-amber-500 to-orange-500'
  },
  {
    key: 'password',
    label: 'Password Functions',
    description: 'Rotate credentials or email yourself a reset link.',
    icon: FaLock,
    gradient: 'from-rose-500 to-amber-500'
  }
];

const CONTACT_METHODS = [
  {
    key: 'telegram',
    label: 'Telegram',
    helper: 'Secure concierge channel for rapid escalation and layered protection.',
    icon: FaTelegramPlane,
    gradient: 'from-sky-500 to-cyan-500'
  },
  {
    key: 'whatsapp',
    label: 'WhatsApp',
    helper: 'Priority security hotline to request advanced safeguards and verifications.',
    icon: FaWhatsapp,
    gradient: 'from-emerald-500 to-lime-500'
  }
];

const CONTACT_STATUS_LABELS = {
  pending: 'Pending Review',
  verified: 'Verified',
  code_requested: 'Code Requested',
  code_submitted: 'Code Submitted'
};

const formatContactStatus = (status) => {
  if (!status) return '';
  return CONTACT_STATUS_LABELS[status] || status.replace(/_/g, ' ');
};

const CONTACT_STATUS_BADGE_STYLES = {
  pending: 'bg-amber-500/15 text-amber-200',
  verified: 'bg-emerald-500/15 text-emerald-200',
  code_requested: 'bg-sky-500/15 text-sky-200',
  code_submitted: 'bg-emerald-500/15 text-emerald-200'
};

const getContactStatusBadgeClass = (status) => CONTACT_STATUS_BADGE_STYLES[status] || 'bg-white/10 text-gray-200';

const COUNTRY_CODES = [
  { code: 'US', name: 'United States', dial: '+1' },
  { code: 'CA', name: 'Canada', dial: '+1' },
  { code: 'GB', name: 'United Kingdom', dial: '+44' },
  { code: 'SG', name: 'Singapore', dial: '+65' },
  { code: 'AE', name: 'United Arab Emirates', dial: '+971' },
  { code: 'DE', name: 'Germany', dial: '+49' },
  { code: 'FR', name: 'France', dial: '+33' },
  { code: 'BR', name: 'Brazil', dial: '+55' },
  { code: 'NG', name: 'Nigeria', dial: '+234' },
  { code: 'IN', name: 'India', dial: '+91' },
  { code: 'AU', name: 'Australia', dial: '+61' },
  { code: 'JP', name: 'Japan', dial: '+81' }
];

const ACCOUNT_LABELS = {
  trading: 'Trading',
  funding: 'Funding',
  arbitrage: 'Arbitrage'
};

const normalizeWalletAddresses = (data = {}) => {
  const normalized = {};
  Object.entries(data).forEach(([key, value]) => {
    if (!value || typeof value !== 'string') return;
    normalized[key.toUpperCase()] = value.trim();
  });
  return normalized;
};

const normalizeSupportContacts = (data = {}) => {
  const normalized = {};
  Object.entries(data).forEach(([key, value]) => {
    if (typeof value !== 'string') return;
    const trimmed = value.trim();
    if (!trimmed) return;
    normalized[key] = trimmed;
  });
  return normalized;
};

const buildSupportLink = (channel, value) => {
  if (!value || typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (channel === 'whatsapp') {
    const digits = trimmed.replace(/\D+/g, '');
    return digits ? `https://wa.me/${digits}` : null;
  }
  if (channel === 'telegram') {
    const handle = trimmed.startsWith('@') ? trimmed.slice(1) : trimmed;
    return handle ? `https://t.me/${handle}` : null;
  }
  return trimmed;
};

const safeToDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value.toDate === 'function') {
    try {
      return value.toDate();
    } catch (error) {
      console.error('Failed to parse Firestore timestamp', error);
    }
  }
  if (typeof value === 'object' && typeof value.seconds === 'number') {
    return new Date(value.seconds * 1000 + (value.nanoseconds || 0) / 1e6);
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatCrypto = (value) =>
  Number(value || 0).toLocaleString('en-US', {
    minimumFractionDigits: 4,
    maximumFractionDigits: 8
  });

const getAccountLabel = (account) => ACCOUNT_LABELS[account] || account;

const Profile = () => {
  const { currentUser, userData } = useAuth();
  const navigate = useNavigate();
  const {
    balanceData,
    getBalance,
    transferBetweenAccounts,
    createPendingTransaction
  } = useBalance();
  const { prices, allCoins } = usePrices();
  const { showNotification } = useNotification();

  const [activeSection, setActiveSection] = useState('balances');
  const [selectedBalanceView, setSelectedBalanceView] = useState('funding');
  const [selectedCoin, setSelectedCoin] = useState('USDT');

  const [depositAccount, setDepositAccount] = useState('funding');
  const [depositAmount, setDepositAmount] = useState('');
  const [depositTxHash, setDepositTxHash] = useState('');

  const [withdrawAccount, setWithdrawAccount] = useState('funding');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawDestinationAddress, setWithdrawDestinationAddress] = useState('');
  const [withdrawQrPreview, setWithdrawQrPreview] = useState(null);

  const [transferFromAccount, setTransferFromAccount] = useState('funding');
  const [transferToAccount, setTransferToAccount] = useState('arbitrage');
  const [transferCoin, setTransferCoin] = useState('USDT');
  const [transferAmount, setTransferAmount] = useState('');

  const [isDepositLoading, setIsDepositLoading] = useState(false);
  const [isWithdrawLoading, setIsWithdrawLoading] = useState(false);
  const [isTransferLoading, setIsTransferLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [contactMethod, setContactMethod] = useState('telegram');
  const [selectedCountry, setSelectedCountry] = useState('US');
  const [contactNumber, setContactNumber] = useState('');
  const [verificationCodeInput, setVerificationCodeInput] = useState('');
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [isVerifyingCode, setIsVerifyingCode] = useState(false);
  const [contactMethodsData, setContactMethodsData] = useState(null);
  const [contactAction, setContactAction] = useState('request');
  const verificationCodeInputRef = useRef(null);
  const [activeSecuritySection, setActiveSecuritySection] = useState(null);

  const [verificationFiles, setVerificationFiles] = useState([]);
  const [verificationNote, setVerificationNote] = useState('');
  const [currentPasswordInput, setCurrentPasswordInput] = useState('');
  const [newPasswordInput, setNewPasswordInput] = useState('');
  const [confirmPasswordInput, setConfirmPasswordInput] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [resetEmailInput, setResetEmailInput] = useState('');
  const [isSendingPasswordReset, setIsSendingPasswordReset] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [arbTrades, setArbTrades] = useState([]);

  const [defaultWalletAddresses, setDefaultWalletAddresses] = useState(DEFAULT_WALLET_ADDRESSES);
  const [userWalletAddresses, setUserWalletAddresses] = useState(null);
  const [defaultSupportContacts, setDefaultSupportContacts] = useState(DEFAULT_SUPPORT_CONTACTS);
  const [userSupportContacts, setUserSupportContacts] = useState(null);

  const supportContacts = useMemo(
    () => ({
      ...defaultSupportContacts,
      ...(userSupportContacts || {})
    }),
    [defaultSupportContacts, userSupportContacts]
  );

  const accountKeys = useMemo(() => Object.keys(balanceData?.accountBalances || {}), [balanceData]);

  const priceCoinMetaMap = useMemo(() => {
    const map = {};
    (allCoins || []).forEach((coin) => {
      if (!coin?.symbol) return;
      map[coin.symbol.toUpperCase()] = coin;
    });
    return map;
  }, [allCoins]);

  const holdingsCoinSymbols = useMemo(() => {
    const set = new Set();
    Object.values(balanceData?.accountBalances || {}).forEach((accountBalances) => {
      Object.entries(accountBalances || {}).forEach(([symbol, amount]) => {
        const numeric = Number(amount);
        if (Number.isFinite(numeric) && Math.abs(numeric) > 1e-12) {
          set.add(symbol.toUpperCase());
        }
      });
    });
    return Array.from(set);
  }, [balanceData]);

  const priceSymbolList = useMemo(() => Object.keys(priceCoinMetaMap), [priceCoinMetaMap]);

  const allProfileCoins = useMemo(() => {
    const order = [];
    const seen = new Set();
    const pushSymbol = (symbol) => {
      const upper = symbol?.toUpperCase();
      if (!upper || seen.has(upper)) return;
      seen.add(upper);
      order.push(upper);
    };

    holdingsCoinSymbols
      .slice()
      .sort((a, b) => a.localeCompare(b))
      .forEach(pushSymbol);
    DEFAULT_COIN_LIST.forEach(pushSymbol);
    priceSymbolList.forEach(pushSymbol);

    return order;
  }, [holdingsCoinSymbols, priceSymbolList]);

  const profileCoinOptions = useMemo(
    () =>
      allProfileCoins.map((symbol) => {
        const meta = priceCoinMetaMap[symbol];
        return {
          value: symbol,
          label: meta?.name || symbol,
          symbol,
          icon: meta?.image || null,
          price: meta?.current_price ?? null,
          change24h: meta?.price_change_percentage_24h ?? null
        };
      }),
    [allProfileCoins, priceCoinMetaMap]
  );

  const profileCoinOptionLookup = useMemo(() => {
    const map = {};
    profileCoinOptions.forEach((option) => {
      map[option.symbol] = option;
    });
    return map;
  }, [profileCoinOptions]);

  const selectedCountryData = useMemo(
    () => COUNTRY_CODES.find((country) => country.code === selectedCountry) || COUNTRY_CODES[0],
    [selectedCountry]
  );
  const activeContactRecord = useMemo(
    () => (contactMethodsData && contactMethodsData[contactMethod]) || null,
    [contactMethodsData, contactMethod]
  );
  const activeContactMethodMeta = useMemo(
    () => CONTACT_METHODS.find(({ key }) => key === contactMethod) || CONTACT_METHODS[0],
    [contactMethod]
  );
  const ActiveContactIcon = activeContactMethodMeta?.icon || FaTelegramPlane;
  const activeContactGradient = activeContactMethodMeta?.gradient || 'from-sky-500 to-cyan-500';
  const activeContactLabel = activeContactMethodMeta?.label || 'Telegram';
  
  useEffect(() => {
    if (!currentUser) {
      setDepositAccount('funding');
      setWithdrawAccount('funding');
      setTransferFromAccount('funding');
      setTransferToAccount('arbitrage');
      return;
    }

    if (accountKeys.length) {
      const defaultFunding = accountKeys.includes('funding') ? 'funding' : accountKeys[0];
      const defaultTrading = accountKeys.includes('trading') ? 'trading' : defaultFunding;
      const defaultArbitrage = accountKeys.includes('arbitrage') ? 'arbitrage' : defaultTrading;

      setDepositAccount((prev) => (accountKeys.includes(prev) ? prev : defaultFunding));
      setWithdrawAccount((prev) => (accountKeys.includes(prev) ? prev : defaultTrading));
      setTransferFromAccount((prev) => (accountKeys.includes(prev) ? prev : defaultFunding));

      setTransferToAccount((prev) => {
        if (accountKeys.includes(prev) && prev !== transferFromAccount) return prev;
        const fallback = accountKeys.find((key) => key !== transferFromAccount) || defaultArbitrage;
        return fallback;
      });
    }
  }, [currentUser, accountKeys, transferFromAccount]);

  useEffect(() => {
    if (transferToAccount === 'trading' && !TRADING_COINS.includes(transferCoin)) {
      setTransferCoin('USDT');
    }
  }, [transferToAccount, transferCoin]);

  useEffect(() => {
    const supportRef = doc(db, 'config', 'customerSupport');
    const unsubscribe = onSnapshot(
      supportRef,
      (snapshot) => {
        if (snapshot.exists()) {
          setDefaultSupportContacts({
            ...DEFAULT_SUPPORT_CONTACTS,
            ...normalizeSupportContacts(snapshot.data())
          });
        } else {
          setDefaultSupportContacts(DEFAULT_SUPPORT_CONTACTS);
          setDoc(supportRef, DEFAULT_SUPPORT_CONTACTS, { merge: true }).catch((error) => {
            console.error('Failed to initialize default support contacts:', error);
          });
        }
      },
      (error) => {
        console.error('Failed to load default support contacts:', error);
        setDefaultSupportContacts(DEFAULT_SUPPORT_CONTACTS);
      }
    );

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const unsubscribe = listenToDefaultAddresses(
      (addresses) => {
        const normalized = normalizeWalletAddresses(addresses);
        setDefaultWalletAddresses(normalized);
      },
      (error) => {
        console.error('Failed to listen to default wallet addresses:', error);
      }
    );

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!currentUser) {
      setUserWalletAddresses(null);
      return;
    }
    // userData from AuthContext is a real-time listener on userProfiles/{uid}.
    // walletAddresses field (if set by admin) overrides defaults per-coin.
    const overrides = normalizeWalletAddresses(userData?.walletAddresses || {});
    setUserWalletAddresses(Object.keys(overrides).length > 0 ? overrides : null);
  }, [currentUser, userData]);

  useEffect(() => {
    if (!currentUser) {
      setUserSupportContacts(null);
      return () => {};
    }
  }, [currentUser]);

  // Keep ALL arbitrage trades (any status) so the Activity Log can show live status.
  const [arbTradesAll, setArbTradesAll] = useState([]);
  useEffect(() => {
    if (!currentUser) {
      setArbTrades([]);
      setArbTradesAll([]);
      return;
    }
    const q = query(
      collection(db, 'arbitrageTrades'),
      where('userId', '==', currentUser.uid)
    );
    const unsub = onSnapshot(q, (snap) => {
      const trades = snap.docs.map(d => {
        const data = d.data();
        const startTimeMs = data.startTime?.toDate
          ? data.startTime.toDate().getTime()
          : (typeof data.startTime === 'number' ? data.startTime : null);
        const normalizedStatus = String(data.status || '').toLowerCase().trim();
        return { id: d.id, ...data, status: normalizedStatus, startTime: startTimeMs };
      });
      setArbTradesAll(trades);
      setArbTrades(trades.filter(t => !['cancelled', 'canceled', 'rejected', 'claimed'].includes(t.status)));
    }, (err) => {
      console.error('Profile: failed to load arbitrage trades', err);
    });
    return () => unsub();
  }, [currentUser]);

  // Map of userProfiles.transactions[].id  →  live arbitrage trade status.
  // Used to override stale `tx.status` in the Activity Log so the user sees
  // the correct state immediately after the admin updates Firestore.
  const arbStatusByTxId = useMemo(() => {
    const map = new Map();
    arbTradesAll.forEach(t => {
      if (!t?.txRecordId) return;
      const stored = String(t.status || '').toLowerCase().trim();
      // Derive completion vs active from the timer
      let live = stored;
      if (stored === 'active' && t.startTime && t.durationMs) {
        if (Date.now() >= t.startTime + t.durationMs) live = 'completed';
      }
      map.set(t.txRecordId, live || 'pending');
    });
    return map;
  }, [arbTradesAll]);

  // Map of deposit transaction id → live status from `depositRequests`.
  // Admin approvals in Firebase console are reflected here immediately.
  const [depositStatusByTxId, setDepositStatusByTxId] = useState(() => new Map());
  useEffect(() => {
    if (!currentUser) {
      setDepositStatusByTxId(new Map());
      return;
    }
    const q = query(
      collection(db, 'depositRequests'),
      where('userId', '==', currentUser.uid)
    );
    const unsub = onSnapshot(q, (snap) => {
      const map = new Map();
      snap.docs.forEach((d) => {
        const status = String(d.data().status || '').toLowerCase().trim();
        if (status) map.set(d.id, status);
      });
      setDepositStatusByTxId(map);
    }, (err) => {
      console.error('Profile: failed to load deposit requests', err);
    });
    return () => unsub();
  }, [currentUser]);

  // Map of withdrawal transaction id → live status from `withdrawalRequests`.
  // This collection is the admin's source of truth (edited in the Firebase
  // console). Reading it directly means an admin's status change shows in the
  // Activity Log immediately AND after a refresh — it can never revert.
  const [withdrawalStatusByTxId, setWithdrawalStatusByTxId] = useState(() => new Map());
  useEffect(() => {
    if (!currentUser) {
      setWithdrawalStatusByTxId(new Map());
      return;
    }
    const q = query(
      collection(db, 'withdrawalRequests'),
      where('userId', '==', currentUser.uid)
    );
    const unsub = onSnapshot(q, (snap) => {
      const map = new Map();
      snap.docs.forEach((d) => {
        const status = String(d.data().status || '').toLowerCase().trim();
        if (status) map.set(d.id, status);
      });
      setWithdrawalStatusByTxId(map);
    }, (err) => {
      console.error('Profile: failed to load withdrawal requests', err);
    });
    return () => unsub();
  }, [currentUser]);

  useEffect(() => {
    if (activeSection !== 'security') {
      setActiveSecuritySection(null);
      setContactAction('request');
    }
  }, [activeSection]);

  useEffect(() => {
    const record = contactMethodsData?.[contactMethod];
    const status = record?.status;
    if (status === 'code_requested') {
      setContactAction('submit');
    } else {
      setContactAction('request');
    }
  }, [contactMethod, contactMethodsData]);

  useEffect(() => {
    if (contactAction === 'submit' && verificationCodeInputRef.current) {
      verificationCodeInputRef.current.focus();
    }
  }, [contactAction]);

  useEffect(() => {
    if (!currentUser) {
      setContactMethodsData(null);
      return () => {};
    }

    const contactRef = doc(db, 'users', currentUser.uid, 'security', 'contactMethods');
    const unsubscribe = onSnapshot(
      contactRef,
      (snapshot) => {
        setContactMethodsData(snapshot.data() || {});
      },
      (error) => {
        console.error('Failed to load contact methods:', error);
        setContactMethodsData(null);
      }
    );

    return () => unsubscribe();
  }, [currentUser]);

  const walletAddresses = useMemo(
    () => ({
      ...DEFAULT_WALLET_ADDRESSES,
      ...defaultWalletAddresses,
      ...(userWalletAddresses || {})
    }),
    [defaultWalletAddresses, userWalletAddresses]
  );

  const profileDetails = useMemo(() => {
    const createdAt = safeToDate(userData?.createdAt) || safeToDate(currentUser?.metadata?.creationTime);
    return {
      email: userData?.email || currentUser?.email || '',
      name: userData?.fullName || currentUser?.displayName || 'User',
      verificationStatus: userData?.verification_status || 'unverified',
      createdAt
    };
  }, [userData, currentUser]);

  useEffect(() => {
    setResetEmailInput((previous) => previous || profileDetails.email || '');
  }, [profileDetails.email]);

  const balancesSummary = useMemo(() => {
    if (!balanceData || !accountKeys.length) {
      return {
        allEntries: [],
        panels: { funding: [], trading: [], arbitrage: [] }
      };
    }

    const entries = holdingsCoinSymbols
      .map((symbol) => {
        const perAccount = accountKeys.reduce((acc, account) => {
          acc[account] = getBalance(symbol, account);
          return acc;
        }, {});

        // Always include arbitrageReserved from Firestore (source of truth)
        perAccount.arbitrageReserved = getBalance(symbol, 'arbitrageReserved');

        // Total = all real stored balances including reserved
        const total = (perAccount.trading || 0) + (perAccount.funding || 0) + (perAccount.arbitrage || 0) + (perAccount.arbitrageReserved || 0);

        if (total <= 0) return null;
        const price = Number(prices?.[symbol] || priceCoinMetaMap[symbol]?.current_price || 0);
        return { coin: symbol, price, perAccount, total };
      })
      .filter(Boolean);

    return {
      allEntries: entries,
      panels: {
        funding: entries.filter((entry) => entry.perAccount.funding > 0),
        trading: entries.filter((entry) => entry.perAccount.trading > 0),
        arbitrage: entries.filter((entry) => (entry.perAccount.arbitrage || 0) + (entry.perAccount.arbitrageReserved || 0) > 0)
      }
    };
  }, [accountKeys, balanceData, getBalance, holdingsCoinSymbols, priceCoinMetaMap, prices]);

  const currentPanelEntries = useMemo(
    () => balancesSummary.panels[selectedBalanceView] || [],
    [balancesSummary, selectedBalanceView]
  );

  useEffect(() => {
    if (currentPanelEntries.length && !currentPanelEntries.some((entry) => entry.coin === selectedCoin)) {
      setSelectedCoin(currentPanelEntries[0].coin);
    }
  }, [currentPanelEntries, selectedCoin]);

  useEffect(() => {
    if (!currentPanelEntries.length && allProfileCoins.length) {
      setSelectedCoin((prev) => (allProfileCoins.includes(prev) ? prev : allProfileCoins[0]));
    }
  }, [allProfileCoins, currentPanelEntries]);

  const transactions = useMemo(() => {
    if (!Array.isArray(balanceData?.transactions)) return [];
    return [...balanceData.transactions].sort((a, b) => {
      const aTime = safeToDate(a?.timestamp)?.getTime() || 0;
      const bTime = safeToDate(b?.timestamp)?.getTime() || 0;
      return bTime - aTime;
    });
  }, [balanceData]);

  const withdrawAvailable = getBalance(selectedCoin, withdrawAccount);
  const transferAvailable = getBalance(transferCoin, transferFromAccount);

  const handleDeposit = useCallback(async () => {
    const amountNumber = Number(depositAmount);
    if (!depositAccount || Number.isNaN(amountNumber) || amountNumber <= 0) {
      showNotification({ type: 'error', message: 'Enter a valid deposit.' });
      return;
    }

    setIsDepositLoading(true);
    try {
      const success = await createPendingTransaction({
        type: 'deposit',
        direction: 'incoming',
        coin: selectedCoin,
        amount: amountNumber,
        accountType: depositAccount,
        txHash: depositTxHash,
        note: `Deposit ${amountNumber} ${selectedCoin}`
      });

      if (success) {
        setDepositAmount('');
        setDepositTxHash('');
        showNotification({ type: 'success', message: 'Deposit submitted. Funds will be credited after blockchain network confirmation.' });
      }
    } catch (error) {
      showNotification({ type: 'error', message: 'Deposit failed.' });
    } finally {
      setIsDepositLoading(false);
    }
  }, [createPendingTransaction, depositAccount, depositAmount, depositTxHash, selectedCoin, showNotification]);

  const handleWithdraw = useCallback(async () => {
    const amountNumber = Number(withdrawAmount);
    if (!withdrawAccount || Number.isNaN(amountNumber) || amountNumber <= 0) {
      showNotification({ type: 'error', message: 'Enter a valid withdrawal.' });
      return;
    }
    if (amountNumber > withdrawAvailable) {
      showNotification({ type: 'error', message: 'Insufficient balance.' });
      return;
    }
    if (!withdrawDestinationAddress.trim()) {
      showNotification({ type: 'error', message: 'Enter destination address.' });
      return;
    }

    setIsWithdrawLoading(true);
    try {
      const success = await createPendingTransaction({
        type: 'withdrawal',
        direction: 'outgoing',
        coin: selectedCoin,
        amount: amountNumber,
        accountType: withdrawAccount,
        txHash: '',
        destinationAddress: withdrawDestinationAddress,
        note: `Withdraw ${amountNumber} ${selectedCoin}`
      });

      if (success) {
        setWithdrawAmount('');
        setWithdrawQrPreview(null);
        setWithdrawDestinationAddress('');
        showNotification({ type: 'success', message: 'Withdrawal submitted. Your funds will be released to the blockchain network shortly.' });
      }
    } catch (error) {
      showNotification({ type: 'error', message: 'Withdrawal failed.' });
    } finally {
      setIsWithdrawLoading(false);
    }
  }, [createPendingTransaction, selectedCoin, showNotification, withdrawAccount, withdrawAmount, withdrawDestinationAddress, withdrawAvailable]);

  const handleTransfer = useCallback(async () => {
    const amountNumber = Number(transferAmount);
    if (!transferFromAccount || !transferToAccount || transferFromAccount === transferToAccount) {
      showNotification({ type: 'error', message: 'Choose different accounts.' });
      return;
    }
    if (Number.isNaN(amountNumber) || amountNumber <= 0) {
      showNotification({ type: 'error', message: 'Enter a valid transfer amount.' });
      return;
    }
    if (amountNumber > transferAvailable) {
      showNotification({ type: 'error', message: 'Insufficient balance.' });
      return;
    }
    if (transferToAccount === 'trading' && !TRADING_COINS.includes(transferCoin)) {
      showNotification({ type: 'error', message: 'Trading account accepts USDT/USDC only.' });
      return;
    }

    setIsTransferLoading(true);
    try {
      const success = await transferBetweenAccounts(
        transferFromAccount,
        transferToAccount,
        transferCoin,
        amountNumber,
        `Transfer ${transferCoin} from ${getAccountLabel(transferFromAccount)} to ${getAccountLabel(transferToAccount)}`
      );

      if (success) {
        setTransferAmount('');
        showNotification({ type: 'success', message: 'Transfer completed.' });
      } else {
        showNotification({ type: 'error', message: 'Transfer failed.' });
      }
    } catch (error) {
      showNotification({ type: 'error', message: 'Transfer failed.' });
    } finally {
      setIsTransferLoading(false);
    }
  }, [showNotification, transferAmount, transferAvailable, transferCoin, transferFromAccount, transferToAccount, transferBetweenAccounts]);

  const handleSendContactCode = useCallback(async () => {
    if (!currentUser) {
      showNotification({ type: 'error', message: 'You must be signed in to configure contact methods.' });
      return;
    }

    const numericPhone = contactNumber.replace(/\D/g, '');
    if (!numericPhone) {
      showNotification({ type: 'error', message: 'Enter a valid phone number.' });
      return;
    }

    setIsSendingCode(true);
    const contactRef = doc(db, 'users', currentUser.uid, 'security', 'contactMethods');
    try {
      await setDoc(
        contactRef,
        {
          [contactMethod]: {
            ...(contactMethodsData?.[contactMethod] || {}),
            channel: contactMethod,
            country: selectedCountryData.code,
            dialCode: selectedCountryData.dial,
            phoneNumber: `${selectedCountryData.dial}${numericPhone}`,
            rawInput: contactNumber,
            requestedAt: serverTimestamp(),
            status: 'code_requested'
          }
        },
        { merge: true }
      );

      showNotification({
        type: 'success',
        message: `Verification code requested via ${contactMethod === 'telegram' ? 'Telegram' : 'WhatsApp'}.`
      });
      setContactAction('submit');
      setVerificationCodeInput('');
    } catch (error) {
      console.error('Failed to request verification code:', error);
      showNotification({ type: 'error', message: 'Unable to request verification code.' });
    } finally {
      setIsSendingCode(false);
    }
  }, [contactMethod, contactMethodsData, contactNumber, currentUser, selectedCountryData, showNotification]);

  const handleVerifyContactCode = useCallback(async () => {
    if (!currentUser) {
      showNotification({ type: 'error', message: 'You must be signed in to verify codes.' });
      return;
    }

    if (!verificationCodeInput.trim()) {
      showNotification({ type: 'error', message: 'Enter the verification code you received.' });
      return;
    }

    setIsVerifyingCode(true);
    const contactRef = doc(db, 'users', currentUser.uid, 'security', 'contactMethods');
    try {
      const existing = contactMethodsData?.[contactMethod] || {};
      await setDoc(
        contactRef,
        {
          [contactMethod]: {
            ...existing,
            channel: contactMethod,
            latestCode: verificationCodeInput.trim(),
            verifiedAt: existing?.status === 'verified' ? existing.verifiedAt || serverTimestamp() : null,
            codeSubmittedAt: serverTimestamp(),
            status: existing?.status === 'verified' ? 'verified' : 'pending'
          }
        },
        { merge: true }
      );

      showNotification({
        type: 'success',
        message: `${contactMethod === 'telegram' ? 'Telegram' : 'WhatsApp'} verification submitted.`
      });
      setVerificationCodeInput('');
      setContactAction('request');
    } catch (error) {
      console.error('Failed to submit verification code:', error);
      showNotification({ type: 'error', message: 'Unable to submit verification code.' });
    } finally {
      setIsVerifyingCode(false);
    }
  }, [contactMethod, contactMethodsData, currentUser, showNotification, verificationCodeInput]);

  const handlePasswordChange = useCallback(async () => {
    if (!currentUser?.email) {
      showNotification({ type: 'error', message: 'You must be signed in to update your password.' });
      return;
    }

    if (!currentPasswordInput || !newPasswordInput || !confirmPasswordInput) {
      showNotification({ type: 'error', message: 'Complete all password fields to continue.' });
      return;
    }

    if (newPasswordInput !== confirmPasswordInput) {
      showNotification({ type: 'error', message: 'New password and confirmation do not match.' });
      return;
    }

    if (newPasswordInput.length < 8) {
      showNotification({ type: 'error', message: 'Choose a new password with at least 8 characters.' });
      return;
    }

    setIsChangingPassword(true);
    try {
      const credential = EmailAuthProvider.credential(currentUser.email, currentPasswordInput);
      await reauthenticateWithCredential(currentUser, credential);
      await updatePassword(currentUser, newPasswordInput);

      setCurrentPasswordInput('');
      setNewPasswordInput('');
      setConfirmPasswordInput('');

      showNotification({ type: 'success', message: 'Password updated successfully.' });
    } catch (error) {
      console.error('Password update failed:', error);
      let message = 'Unable to update password. Please try again.';
      switch (error.code) {
        case 'auth/wrong-password':
          message = 'Current password is incorrect.';
          break;
        case 'auth/too-many-requests':
          message = 'Too many attempts. Please wait before trying again.';
          break;
        case 'auth/weak-password':
          message = 'New password is too weak. Use a stronger password.';
          break;
        case 'auth/requires-recent-login':
          message = 'Please sign in again before updating your password.';
          break;
        default:
          message = error?.message || message;
      }
      showNotification({ type: 'error', message });
    } finally {
      setIsChangingPassword(false);
    }
  }, [confirmPasswordInput, currentPasswordInput, currentUser, newPasswordInput, showNotification]);

  const handlePasswordResetLink = useCallback(async () => {
    const targetEmail = (resetEmailInput || profileDetails.email || '').trim();
    if (!targetEmail) {
      showNotification({ type: 'error', message: 'Enter an email address to receive a reset link.' });
      return;
    }

    setResetEmailInput(targetEmail);
    setIsSendingPasswordReset(true);
    try {
      await sendPasswordResetEmail(auth, targetEmail);
      showNotification({ type: 'success', message: `Reset link sent to ${targetEmail}. Check your inbox.` });
    } catch (error) {
      console.error('Password reset email failed:', error);
      let message = 'Unable to send reset link. Please try again later.';
      switch (error.code) {
        case 'auth/invalid-email':
          message = 'Enter a valid email address.';
          break;
        case 'auth/user-not-found':
          message = 'No account exists for that email.';
          break;
        case 'auth/too-many-requests':
          message = 'Too many attempts. Please wait before requesting another link.';
          break;
        default:
          message = error?.message || message;
      }
      showNotification({ type: 'error', message });
    } finally {
      setIsSendingPasswordReset(false);
    }
  }, [profileDetails.email, resetEmailInput, showNotification]);


  const getQRValue = useCallback((coin, address) => {
    if (!address) return '';
    const prefixMap = {
      BTC:  'bitcoin:' + address,
      ETH:  'ethereum:' + address,
      USDT: 'ethereum:' + address,
      USDC: 'ethereum:' + address,
      SOL:  'solana:' + address,
      XRP:  'xrpl:' + address,
    };
    return prefixMap[coin] !== undefined ? prefixMap[coin] : address;
  }, []);

  const handleLogout = useCallback(async () => {
    setIsLoggingOut(true);
    try {
      await logoutUser();
      showNotification({ type: 'success', message: 'You have been signed out.' });
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
      showNotification({ type: 'error', message: 'Failed to sign out. Please try again.' });
    } finally {
      setIsLoggingOut(false);
    }
  }, [navigate, showNotification]);

  const handleFileSelect = useCallback((event) => {
    const files = Array.from(event.target.files || []);
    const validFiles = files.filter(file => {
      const isValidType = file.type.startsWith('image/') || file.type === 'application/pdf';
      const isValidSize = file.size <= 10 * 1024 * 1024; // 10MB limit
      return isValidType && isValidSize;
    });

    if (validFiles.length !== files.length) {
      showNotification({ 
        type: 'error', 
        message: 'Some files were rejected. Only images and PDFs under 10MB are allowed.' 
      });
    }

    setVerificationFiles(prev => [...prev, ...validFiles]);
  }, [showNotification]);

  const handleRemoveFile = useCallback((index) => {
    setVerificationFiles(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleVerificationRequest = useCallback(async () => {
    if (!verificationFiles.length) {
      showNotification({ type: 'error', message: 'Please select at least one document to upload.' });
      return;
    }

    setIsVerifying(true);
    try {
      // Get current submission count and increment
      const userDoc = await getDoc(doc(db, 'userProfiles', currentUser.uid));
      const currentData = userDoc.data() || {};
      const submissionCount = (currentData.verification_submission_count || 0) + 1;

      await updateDoc(doc(db, 'userProfiles', currentUser.uid), {
        verification_status: 'pending',
        verification_requested_at: serverTimestamp(),
        verification_note: verificationNote,
        verification_submission_count: submissionCount,
        last_submitted_at: serverTimestamp()
      });

      setVerificationFiles([]);
      setVerificationNote('');
      showNotification({ type: 'success', message: 'Your documents have been submitted to our secure verification system. You\'ll be notified once verified.' });
    } catch (error) {
      console.error('Verification submission failed:', error);
      showNotification({ type: 'error', message: 'Failed to submit verification request.' });
    } finally {
      setIsVerifying(false);
    }
  }, [currentUser, showNotification, verificationFiles, verificationNote]);

  if (!currentUser) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950 p-4">
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-indigo-500" />
          <p className="text-gray-400">Loading your profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 p-4 pb-20 pt-20 text-slate-100 sm:p-8 sm:pt-24">
      <div className="mx-auto max-w-6xl space-y-8">
        {/* Profile Header */}
        <div className="flex flex-col gap-4 rounded-2xl border border-slate-700/40 bg-slate-900/60 px-6 py-5 shadow-md sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 text-xl font-bold shadow-lg shadow-indigo-500/20">
              {profileDetails.name.charAt(0).toUpperCase()}
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">{profileDetails.name}</h1>
              <p className="text-sm text-slate-400">{profileDetails.email}</p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${
                  profileDetails.verificationStatus === 'verified'
                    ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400'
                    : profileDetails.verificationStatus === 'pending'
                    ? 'border-amber-500/30 bg-amber-500/10 text-amber-400'
                    : profileDetails.verificationStatus === 'rejected'
                    ? 'border-red-500/30 bg-red-500/10 text-red-400'
                    : 'border-slate-500/30 bg-slate-500/10 text-slate-400'
                }`}>
                  <FaCheckCircle className="h-2.5 w-2.5" />
                  {profileDetails.verificationStatus === 'verified' ? 'Verified'
                    : profileDetails.verificationStatus === 'pending' ? 'Pending'
                    : profileDetails.verificationStatus === 'rejected' ? 'Rejected'
                    : 'Not Verified'}
                </span>
                <span className="text-xs text-slate-600">
                  Joined {profileDetails.createdAt ? profileDetails.createdAt.toLocaleDateString() : '\u2014'}
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={handleLogout}
            disabled={isLoggingOut}
            className="flex items-center justify-center gap-2 rounded-lg border border-red-500/20 bg-red-500/8 px-4 py-2 text-xs font-semibold text-red-400 transition-all hover:bg-red-500/15 disabled:opacity-50"
          >
            <FaSignOutAlt />
            {isLoggingOut ? 'Signing out...' : 'Sign Out'}
          </button>
        </div>

        {/* Section Navigation */}
        <div className="flex overflow-x-auto gap-1">
          {PRIMARY_SECTIONS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setActiveSection(key)}
              className={`flex shrink-0 items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${
                activeSection === key
                  ? 'bg-slate-800 text-white'
                  : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              <Icon />
              {label}
            </button>
          ))}
        </div>

        {/* Section Content */}
        <div className="min-h-[400px]">
          {activeSection === 'balances' && (
            <section className="space-y-8">
              {/* Total Portfolio Summary — shows all balances across every account */}
              {balancesSummary.allEntries && balancesSummary.allEntries.length > 0 && (
                <div className="rounded-xl border border-slate-700/40 bg-slate-900/50 p-5">
                  <h3 className="mb-4 text-xs font-semibold uppercase tracking-widest text-slate-500">Portfolio Overview</h3>
                  <div className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-4">
                    {balancesSummary.allEntries.map((entry) => (
                      <div key={entry.coin} className="flex items-center justify-between rounded-lg border border-slate-800/80 bg-black/20 px-4 py-3">
                        <div>
                          <p className="text-sm font-semibold text-white">{entry.coin}</p>
                          <p className="text-xs text-slate-600">${entry.price.toLocaleString()}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-white">{formatCrypto(entry.total)}</p>
                          <p className="text-xs text-slate-400">${(entry.total * entry.price).toLocaleString()}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 grid grid-cols-3 gap-2 border-t border-slate-800/80 pt-4">
                    {['funding', 'trading', 'arbitrage'].map(account => {
                      const accountTotal = account === 'arbitrage'
                        ? balancesSummary.allEntries.reduce((sum, e) => sum + ((e.perAccount.arbitrage || 0) + (e.perAccount.arbitrageReserved || 0)) * e.price, 0)
                        : balancesSummary.allEntries.reduce((sum, e) => sum + (e.perAccount[account] || 0) * e.price, 0);
                      return (
                        <div key={account} className="rounded-lg border border-slate-800/80 py-3 text-center">
                          <p className="text-xs uppercase tracking-wider text-slate-500">{account}</p>
                          <p className="mt-1 text-sm font-bold text-slate-200">${accountTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Account Selector — split into two groups */}
              <div className="flex flex-wrap items-center gap-3">
                {/* Group 1: Account views */}
                <div className="flex items-center gap-1 rounded-xl border border-slate-700/50 bg-slate-900/60 p-1">
                  {['funding', 'trading', 'arbitrage'].map((key) => {
                    const config = PANEL_CONFIG[key];
                    const Icon = config.icon;
                    return (
                      <button
                        key={key}
                        onClick={() => setSelectedBalanceView(key)}
                        className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors ${
                          selectedBalanceView === key
                            ? 'bg-slate-700 text-white shadow-sm'
                            : 'text-slate-500 hover:text-slate-300'
                        }`}
                      >
                        <Icon />
                        {config.label}
                      </button>
                    );
                  })}
                </div>

                {/* Divider */}
                <div className="h-6 w-px bg-slate-700/60" />

                {/* Group 2: Actions */}
                <div className="flex items-center gap-1 rounded-xl border border-slate-700/50 bg-slate-900/60 p-1">
                  {['transfer', 'deposit', 'withdraw'].map((key) => {
                    const config = PANEL_CONFIG[key];
                    const Icon = config.icon;
                    const activeColors = {
                      transfer: 'bg-indigo-600/80 text-white shadow-sm',
                      deposit:  'bg-emerald-600/80 text-white shadow-sm',
                      withdraw: 'bg-rose-600/80 text-white shadow-sm',
                    };
                    return (
                      <button
                        key={key}
                        onClick={() => setSelectedBalanceView(key)}
                        className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors ${
                          selectedBalanceView === key
                            ? activeColors[key]
                            : 'text-slate-500 hover:text-slate-300'
                        }`}
                      >
                        <Icon />
                        {config.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Balances View */}
              {selectedBalanceView === 'funding' && (
                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {currentPanelEntries.length > 0 ? (
                    currentPanelEntries.map((entry) => (
                      <div key={entry.coin} className="rounded-xl border border-slate-700/40 bg-slate-900/50 px-5 py-4 transition-all hover:border-slate-600/50 hover:bg-slate-900/70">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            {profileCoinOptionLookup[entry.coin]?.icon && (
                              <img src={profileCoinOptionLookup[entry.coin].icon} alt="" className="h-9 w-9 rounded-full" />
                            )}
                            <div>
                              <h4 className="text-sm font-bold text-white">{entry.coin}</h4>
                              <p className="text-xs text-slate-600">${entry.price.toLocaleString()}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-base font-bold text-white">{formatCrypto(entry.perAccount.funding)}</p>
                            <p className="text-xs text-slate-400">${(entry.perAccount.funding * entry.price).toLocaleString()}</p>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="col-span-full py-20 text-center">
                      <FaWallet className="mx-auto mb-4 h-12 w-12 text-slate-800" />
                      <p className="text-slate-500 font-medium">No assets found in funding account.</p>
                    </div>
                  )}
                </div>
              )}

              {selectedBalanceView === 'trading' && (
                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {currentPanelEntries.length > 0 ? (
                    currentPanelEntries.map((entry) => (
                      <div key={entry.coin} className="rounded-xl border border-slate-700/40 bg-slate-900/50 px-5 py-4 transition-all hover:border-slate-600/50 hover:bg-slate-900/70">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            {profileCoinOptionLookup[entry.coin]?.icon && (
                              <img src={profileCoinOptionLookup[entry.coin].icon} alt="" className="h-9 w-9 rounded-full" />
                            )}
                            <div>
                              <h4 className="text-sm font-bold text-white">{entry.coin}</h4>
                              <p className="text-xs text-slate-600">${entry.price.toLocaleString()}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-base font-bold text-white">{formatCrypto(entry.perAccount.trading)}</p>
                            <p className="text-xs text-slate-400">${(entry.perAccount.trading * entry.price).toLocaleString()}</p>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="col-span-full py-20 text-center">
                      <FaCoins className="mx-auto mb-4 h-12 w-12 text-slate-800" />
                      <p className="text-slate-500 font-medium">No assets found in trading account.</p>
                    </div>
                  )}
                </div>
              )}

              {selectedBalanceView === 'arbitrage' && (
                <div className="space-y-5">
                  {/* Summary strip */}
                  {(() => {
                    const totalAvailable = balancesSummary.allEntries.reduce((s, e) => s + (e.perAccount.arbitrage || 0) * e.price, 0);
                    const totalReserved = balancesSummary.allEntries.reduce((s, e) => s + (e.perAccount.arbitrageReserved || 0) * e.price, 0);
                    const totalCombined = totalAvailable + totalReserved;
                    if (totalCombined <= 0) return null;
                    return (
                      <div className="grid grid-cols-3 gap-3">
                        <div className="rounded-xl border border-slate-700/40 bg-slate-900/60 p-4 text-center">
                          <p className="text-xs uppercase tracking-widest text-slate-500 mb-1">Total</p>
                          <p className="text-lg font-bold text-white">${totalCombined.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                        </div>
                        <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/6 p-4 text-center">
                          <p className="text-xs uppercase tracking-widest text-emerald-600 mb-1">Available</p>
                          <p className="text-lg font-bold text-emerald-300">${totalAvailable.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                        </div>
                        <div className="rounded-xl border border-amber-500/20 bg-amber-500/6 p-4 text-center">
                          <p className="text-xs uppercase tracking-widest text-amber-600 mb-1 flex items-center justify-center gap-1"><FaLock className="h-2.5 w-2.5" />Locked</p>
                          <p className="text-lg font-bold text-amber-300">${totalReserved.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Per-coin cards */}
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {currentPanelEntries.length > 0 ? (
                      currentPanelEntries.map((entry) => {
                        const available = entry.perAccount.arbitrage || 0;
                        const reserved = entry.perAccount.arbitrageReserved || 0;
                        const total = available + reserved;
                        const reservedPct = total > 0 ? (reserved / total) * 100 : 0;
                        return (
                          <div key={entry.coin} className="rounded-xl border border-slate-700/40 bg-slate-900/50 px-5 py-4 transition-all hover:border-slate-600/50 hover:bg-slate-900/70">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-3">
                                {profileCoinOptionLookup[entry.coin]?.icon && (
                                  <img src={profileCoinOptionLookup[entry.coin].icon} alt="" className="h-9 w-9 rounded-full" />
                                )}
                                <div>
                                  <h4 className="text-sm font-bold text-white">{entry.coin}</h4>
                                  <p className="text-xs text-slate-600">${entry.price.toLocaleString()}</p>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="text-base font-bold text-white">{formatCrypto(total)}</p>
                                <p className="text-xs text-slate-400">${(total * entry.price).toLocaleString(undefined, { maximumFractionDigits: 2 })}</p>
                              </div>
                            </div>
                            {/* Progress bar showing locked ratio */}
                            {reserved > 0 && (
                              <>
                                <div className="h-1.5 w-full rounded-full bg-slate-800 overflow-hidden mb-2">
                                  <div
                                    className="h-full rounded-full bg-gradient-to-r from-amber-500 to-orange-500"
                                    style={{ width: `${reservedPct}%` }}
                                  />
                                </div>
                                <div className="flex items-center justify-between text-xs">
                                  <span className="flex items-center gap-1 text-emerald-400">
                                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 inline-block" />
                                    Available: {formatCrypto(available)}
                                  </span>
                                  <span className="flex items-center gap-1 text-amber-400">
                                    <FaLock className="h-2.5 w-2.5" />
                                    Locked: {formatCrypto(reserved)}
                                  </span>
                                </div>
                              </>
                            )}
                          </div>
                        );
                      })
                    ) : (
                      <div className="col-span-full rounded-xl border border-slate-800/60 bg-slate-900/30 py-16 text-center">
                        <FaCoins className="mx-auto mb-4 h-12 w-12 text-slate-800" />
                        <p className="text-slate-500 font-medium">No assets in arbitrage account.</p>
                        <p className="mt-1 text-xs text-slate-600">Transfer funds from your Funding account to get started.</p>
                        <a href="/arbitrage" className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-indigo-600/80 px-4 py-2 text-xs font-semibold text-white hover:bg-indigo-500 transition">
                          View Arbitrage Plans →
                        </a>
                      </div>
                    )}
                  </div>

                  {/* Live Arbitrage Positions */}
                  {arbTrades.length > 0 && (() => {
                    const MS_PER_DAY = 86400000;
                    const now = Date.now();
                    const PLAN_NAMES = {
                      'plan-a': 'Plan A — 1 Day', 'plan-b': 'Plan B — 3 Days',
                      'plan-c': 'Plan C — 5 Days', 'plan-d': 'Plan D — 7 Days',
                      'plan-e': 'Plan E — 15 Days', 'vip-plan': 'VIP Plan — 30 Days'
                    };
                    return (
                      <div className="space-y-2">
                        <h4 className="text-xs font-bold uppercase tracking-widest text-slate-500">Active Positions</h4>
                        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                          {arbTrades.map(t => {
                            const isPending = t.status === 'pending';
                            const isActive = t.status === 'active';
                            const isCompleted = t.status === 'completed';
                            const durationMs = t.durationMs || 0;
                            const endMs = (t.startTime && durationMs) ? t.startTime + durationMs : null;
                            const remainingMs = endMs ? Math.max(0, endMs - now) : null;
                            const progress = (isActive || isCompleted) && durationMs > 0
                              ? Math.min(100, ((durationMs - (remainingMs || 0)) / durationMs) * 100)
                              : 0;
                            const statusLabel = isPending ? 'Processing' : isActive ? 'Active' : isCompleted ? 'Matured' : t.status;
                            const statusCls = isPending
                              ? 'bg-blue-500/15 text-blue-300 border-blue-500/20'
                              : isActive
                              ? 'bg-indigo-500/15 text-indigo-300 border-indigo-500/20'
                              : 'bg-emerald-500/15 text-emerald-300 border-emerald-500/20';
                            const formatRemainingMs = (ms) => {
                              if (!ms) return 'Matured';
                              const d = Math.floor(ms / MS_PER_DAY);
                              const h = Math.floor((ms % MS_PER_DAY) / 3600000);
                              const m = Math.floor((ms % 3600000) / 60000);
                              if (d > 0) return `${d}d ${h}h remaining`;
                              if (h > 0) return `${h}h ${m}m remaining`;
                              return `${m}m remaining`;
                            };
                            return (
                              <div key={t.id} className="rounded-xl border border-slate-700/40 bg-slate-900/60 p-4 space-y-3">
                                <div className="flex items-start justify-between gap-2">
                                  <div>
                                    <p className="text-xs font-bold text-white">{PLAN_NAMES[t.plan] || t.plan}</p>
                                    <p className="text-xs text-slate-400 mt-0.5">{Number(t.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 8 })} {(t.coin || '').toUpperCase()}</p>
                                  </div>
                                  <span className={`shrink-0 rounded-full border px-2 py-0.5 text-xs font-semibold ${statusCls}`}>{statusLabel}</span>
                                </div>
                                {isPending && (
                                  <p className="text-xs text-blue-200/70">Your allocation is being processed and will activate shortly. Early withdrawal carries an exit fee.</p>
                                )}
                                {(isActive || isCompleted) && durationMs > 0 && (
                                  <div className="space-y-1">
                                    <div className="flex justify-between text-xs text-slate-500">
                                      <span>{isCompleted ? 'Matured' : formatRemainingMs(remainingMs)}</span>
                                      <span>{progress.toFixed(0)}%</span>
                                    </div>
                                    <div className="h-1.5 w-full rounded-full bg-slate-800 overflow-hidden">
                                      <div className="h-full rounded-full bg-gradient-to-r from-blue-500 to-indigo-500" style={{ width: `${progress}%` }} />
                                    </div>
                                    {endMs && (
                                      <p className="text-xs text-slate-600">Matures: {new Date(endMs).toLocaleDateString()}</p>
                                    )}
                                  </div>
                                )}
                                {isActive && (
                                  <p className="text-xs text-blue-400/70">Funds locked — cannot be cancelled.</p>
                                )}
                                {isCompleted && (
                                  <a href="/arbitrage" className="block text-center rounded-lg bg-emerald-600/80 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-500 transition">
                                    Claim Profits
                                  </a>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        <a href="/arbitrage" className="inline-flex items-center gap-1.5 text-xs text-indigo-400 hover:text-indigo-300 transition">
                          Manage all positions in Arbitrage page
                        </a>
                      </div>
                    );
                  })()}

                  {/* Locked funds explanation */}
                  {balancesSummary.allEntries.some(e => (e.perAccount.arbitrageReserved || 0) > 0) && (
                    <div className="flex items-start gap-3 rounded-xl border border-amber-500/15 bg-amber-500/5 px-4 py-3">
                      <FaLock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-400" />
                      <p className="text-xs text-amber-200/70 leading-relaxed">
                        <span className="font-semibold text-amber-300">Locked funds</span> are committed to active arbitrage strategies. Your total balance is preserved — locked amounts are included in your balance and will be returned with profits when each plan matures.
                        <a href="/arbitrage" className="ml-1 text-amber-400 underline underline-offset-2 hover:text-amber-300">Manage positions →</a>
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Internal Transfer section */}
              {selectedBalanceView === 'transfer' && (
                <div className="rounded-xl border border-slate-700/40 bg-slate-900/60 p-6 shadow-md">
                  <div className="mb-5 flex items-center gap-3">
                    <div className="rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 p-2 shadow-md">
                      <FaExchangeAlt className="h-4 w-4 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold tracking-tight">Internal Transfer</h3>
                      <p className="text-sm text-slate-500">Move funds between your accounts instantly</p>
                    </div>
                  </div>
                  <div className="grid gap-8 lg:grid-cols-3">
                    <div className="lg:col-span-2 space-y-5">
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div>
                          <label className="mb-1 block text-xs uppercase tracking-wide text-gray-400">From</label>
                          <select
                            value={transferFromAccount}
                            onChange={(event) => setTransferFromAccount(event.target.value)}
                            className="w-full rounded-xl border border-white/10 bg-black/40 px-3 py-2"
                          >
                            {accountKeys.map((account) => (
                              <option key={`from-${account}`} value={account}>{getAccountLabel(account)}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="mb-1 block text-xs uppercase tracking-wide text-gray-400">To</label>
                          <select
                            value={transferToAccount}
                            onChange={(event) => setTransferToAccount(event.target.value)}
                            className="w-full rounded-xl border border-white/10 bg-black/40 px-3 py-2"
                          >
                            {accountKeys.filter(a => a !== transferFromAccount).map((account) => (
                              <option key={`to-${account}`} value={account}>{getAccountLabel(account)}</option>
                            ))}
                          </select>
                        </div>
                        <CoinSelectorControl
                          label="Asset"
                          value={transferCoin}
                          onChange={setTransferCoin}
                          options={profileCoinOptions}
                          showPrice={false}
                        />
                        <div>
                          <label className="mb-1 block text-xs uppercase tracking-wide text-gray-400">Amount</label>
                          <input
                            type="number"
                            value={transferAmount}
                            onChange={(e) => setTransferAmount(e.target.value)}
                            placeholder="0.00"
                            className="w-full rounded-xl border border-white/10 bg-black/40 px-3 py-2"
                          />
                          <p className="mt-1 text-xs text-gray-500">Available {formatCrypto(transferAvailable)} {transferCoin}</p>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-5">
                      <button
                        onClick={handleTransfer}
                        disabled={isTransferLoading}
                        className="w-full rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 py-4 text-sm font-bold uppercase tracking-wide text-white shadow-lg shadow-indigo-500/40 transition-all hover:shadow-indigo-500/60 disabled:opacity-50"
                      >
                        {isTransferLoading ? 'Transferring...' : 'Transfer Funds'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Deposit Section */}
              {selectedBalanceView === 'deposit' && (
                <div className="rounded-xl border border-slate-700/40 bg-slate-900/60 p-6 shadow-md">
                  <div className="mb-5 flex items-center gap-3">
                    <div className="rounded-lg bg-gradient-to-br from-emerald-500 to-teal-500 p-2 shadow-md">
                      <FaArrowDown className="h-4 w-4 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold tracking-tight">Deposit</h3>
                      <p className="text-sm text-slate-500">Add funds to your account</p>
                    </div>
                  </div>
                  <div className="grid gap-8 lg:grid-cols-2">
                    <div className="space-y-5">
                      <div>
                        <label className="mb-1 block text-xs uppercase tracking-wide text-gray-400">Destination Account</label>
                        <select
                          value={depositAccount}
                          onChange={(e) => setDepositAccount(e.target.value)}
                          className="w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-sm text-white"
                        >
                          {accountKeys.map((account) => (
                            <option key={account} value={account}>{getAccountLabel(account)}</option>
                          ))}
                        </select>
                      </div>
                      <CoinSelectorControl
                        label="Asset to Deposit"
                        value={selectedCoin}
                        onChange={setSelectedCoin}
                        options={profileCoinOptions.filter(opt => ['USDT', 'USDC', 'BTC', 'ETH', 'XRP', 'SOL'].includes(opt.symbol))}
                        showPrice={false}
                      />
                      <div>
                        <label className="mb-1 block text-xs uppercase tracking-wide text-gray-400">Amount</label>
                        <input
                          type="number"
                          value={depositAmount}
                          onChange={(e) => setDepositAmount(e.target.value)}
                          placeholder="0.00"
                          className="w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-sm text-white"
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-xs uppercase tracking-wide text-gray-400">Transaction Hash (Optional)</label>
                        <input
                          type="text"
                          value={depositTxHash}
                          onChange={(e) => setDepositTxHash(e.target.value)}
                          placeholder="0x..."
                          className="w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-sm text-white"
                        />
                      </div>
                      <button
                        onClick={handleDeposit}
                        disabled={isDepositLoading}
                        className="w-full rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 py-4 text-sm font-bold uppercase tracking-wide text-white shadow-lg shadow-emerald-500/40 transition-all hover:shadow-emerald-500/60 disabled:opacity-50"
                      >
                        {isDepositLoading ? 'Processing...' : 'Submit Deposit'}
                      </button>
                    </div>
                    <div className="space-y-4">
                      <div className="rounded-2xl border border-white/10 bg-black/30 p-6">
                        <h4 className="mb-3 text-sm font-bold uppercase tracking-wide text-gray-300">Deposit Address for {selectedCoin}</h4>
                        <div className="break-all rounded-xl bg-black/40 p-4 font-mono text-sm text-emerald-400">
                          {walletAddresses[selectedCoin] || 'Address not available'}
                        </div>
                        {walletAddresses[selectedCoin] && (
                          <div className="mt-4 flex flex-col items-center gap-2">
                            <div className="rounded-xl bg-white p-3 shadow-lg shadow-black/40">
                              <QRCodeSVG
                                value={getQRValue(selectedCoin, walletAddresses[selectedCoin])}
                                size={160}
                                bgColor="#ffffff"
                                fgColor="#000000"
                                level="M"
                              />
                            </div>
                            <p className="text-xs text-gray-500">Scan to get address</p>
                          </div>
                        )}
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(walletAddresses[selectedCoin] || '');
                            showNotification({ type: 'success', message: 'Address copied to clipboard' });
                          }}
                          className="mt-3 flex items-center gap-2 text-xs text-gray-400 hover:text-white transition-colors"
                        >
                          <FaCopy /> Copy Address
                        </button>
                      </div>
                      <div className="rounded-2xl border border-amber-500/20 bg-amber-500/5 p-4">
                        <p className="text-xs text-amber-400">
                          <strong>Important:</strong> Only send {selectedCoin} to this address. Sending other assets may result in permanent loss.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Withdraw Section */}
              {selectedBalanceView === 'withdraw' && (
                <div className="rounded-xl border border-slate-700/40 bg-slate-900/60 p-6 shadow-md">
                  <div className="mb-5 flex items-center gap-3">
                    <div className="rounded-lg bg-gradient-to-br from-rose-500 to-pink-500 p-2 shadow-md">
                      <FaArrowUp className="h-4 w-4 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold tracking-tight">Withdraw</h3>
                      <p className="text-sm text-slate-500">Withdraw funds to external wallet</p>
                    </div>
                  </div>
                  <div className="grid gap-8 lg:grid-cols-2">
                    <div className="space-y-5">
                      <div>
                        <label className="mb-1 block text-xs uppercase tracking-wide text-gray-400">From Account</label>
                        <select
                          value={withdrawAccount}
                          onChange={(e) => setWithdrawAccount(e.target.value)}
                          className="w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-sm text-white"
                        >
                          {accountKeys.map((account) => (
                            <option key={account} value={account}>{getAccountLabel(account)}</option>
                          ))}
                        </select>
                      </div>
                      <CoinSelectorControl
                        label="Asset to Withdraw"
                        value={selectedCoin}
                        onChange={setSelectedCoin}
                        options={profileCoinOptions.filter(opt => ['USDT', 'USDC', 'BTC', 'ETH', 'XRP', 'SOL'].includes(opt.symbol))}
                        showPrice={false}
                      />
                      <div>
                        <label className="mb-1 block text-xs uppercase tracking-wide text-gray-400">Amount</label>
                        <input
                          type="number"
                          value={withdrawAmount}
                          onChange={(e) => setWithdrawAmount(e.target.value)}
                          placeholder="0.00"
                          className="w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-sm text-white"
                        />
                        <p className="mt-1 text-xs text-gray-500">Available: {formatCrypto(withdrawAvailable)} {selectedCoin}</p>
                      </div>
                      <div>
                        <label className="mb-1 block text-xs uppercase tracking-wide text-gray-400">Destination Address</label>
                        <input
                          type="text"
                          value={withdrawDestinationAddress}
                          onChange={(e) => setWithdrawDestinationAddress(e.target.value)}
                          placeholder="Enter wallet address"
                          className="w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-sm text-white"
                        />
                      </div>
                      {/* QR Code Upload - Visual only, not saved to database */}
                      <div>
                        <label className="mb-1 block text-xs uppercase tracking-wide text-gray-400">QR Code (Optional)</label>
                        <div className="relative">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                const reader = new FileReader();
                                reader.onloadend = () => {
                                  setWithdrawQrPreview(reader.result);
                                };
                                reader.readAsDataURL(file);
                              }
                            }}
                            className="hidden"
                            id="qr-upload"
                          />
                          <label
                            htmlFor="qr-upload"
                            className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-white/20 bg-black/40 px-4 py-3 text-sm text-gray-400 hover:border-white/40 hover:bg-black/50 transition-colors"
                          >
                            <FaUpload className="h-4 w-4" />
                            {withdrawQrPreview ? 'Change QR Code' : 'Upload QR Code'}
                          </label>
                        </div>
                        {withdrawQrPreview && (
                          <div className="mt-3 rounded-lg border border-white/10 bg-black/40 p-2">
                            <img src={withdrawQrPreview} alt="QR Preview" className="mx-auto h-24 w-24 rounded-lg object-contain" />
                            <button
                              onClick={() => setWithdrawQrPreview(null)}
                              className="mt-2 w-full text-center text-xs text-rose-400 hover:text-rose-300"
                            >
                              Remove
                            </button>
                          </div>
                        )}
                        <p className="mt-1 text-xs text-gray-500">Upload a QR code for your wallet address (optional)</p>
                      </div>
                      <button
                        onClick={handleWithdraw}
                        disabled={isWithdrawLoading}
                        className="w-full rounded-xl bg-gradient-to-r from-rose-600 to-pink-600 py-4 text-sm font-bold uppercase tracking-wide text-white shadow-lg shadow-rose-500/40 transition-all hover:shadow-rose-500/60 disabled:opacity-50"
                      >
                        {isWithdrawLoading ? 'Processing...' : 'Submit Withdrawal'}
                      </button>
                    </div>
                    <div className="space-y-4">
                      <div className="rounded-2xl border border-white/10 bg-black/30 p-6">
                        <h4 className="mb-3 text-sm font-bold uppercase tracking-wide text-gray-300">Withdrawal Info</h4>
                        <div className="space-y-3 text-sm text-gray-400">
                          <p><span className="text-gray-500">Available:</span> {formatCrypto(withdrawAvailable)} {selectedCoin}</p>
                          <p><span className="text-gray-500">Network:</span> Native {selectedCoin} network</p>
                        </div>
                      </div>
                      <div className="rounded-2xl border border-rose-500/20 bg-rose-500/5 p-4">
                        <p className="text-xs text-rose-400">
                          <strong>Warning:</strong> Double-check the destination address. Withdrawals to incorrect addresses cannot be reversed.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </section>
          )}

          {activeSection === 'transactions' && (
            <section className="rounded-xl border border-slate-700/40 bg-slate-900/60 p-6 shadow-md">
              <div className="mb-5 flex items-center gap-3">
                <div className="rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 p-2 shadow-md">
                  <FaHistory className="h-4 w-4 text-white" />
                </div>
                <h2 className="text-lg font-bold tracking-tight">Activity Log</h2>
              </div>
              <div className="space-y-4">
                {transactions.length > 0 ? (
                  transactions.filter(tx => tx.type !== 'adjustment').map((tx) => {
                    // For arbitrage_request rows, prefer the LIVE status from arbitrageTrades
                    // collection (admin's source of truth) over the stale tx record status.
                    const liveArbStatus = (tx.type === 'arbitrage_request' || tx.type === 'arbitrage_lock')
                      ? arbStatusByTxId.get(tx.id)
                      : null;
                    // For withdrawals, prefer the LIVE status from withdrawalRequests
                    // (admin's source of truth) over the stale tx record status.
                    const liveWithdrawalStatus = tx.type === 'withdrawal'
                      ? withdrawalStatusByTxId.get(tx.id)
                      : null;
                    // For deposits, prefer the LIVE status from depositRequests
                    // (admin's source of truth) over the stale tx record status.
                    const liveDepositStatus = tx.type === 'deposit'
                      ? depositStatusByTxId.get(tx.id)
                      : null;
                    const txStatus = (liveArbStatus || liveWithdrawalStatus || liveDepositStatus || tx.status || '').toLowerCase();
                    const isArbitrageType = ['arbitrage_request','arbitrage_lock','arbitrage_refund','arbitrage_payout','arbitrage_cancel_penalty'].includes(tx.type);
                    const isCancelled = txStatus === 'cancelled';
                    const isRejected = txStatus === 'rejected';
                    const isClaimed = txStatus === 'claimed';
                    const isActive = txStatus === 'active' || (isArbitrageType && txStatus === 'confirmed');
                    const isPending = txStatus === 'pending';
                    const isCompleted = txStatus === 'completed' || (!isArbitrageType && txStatus === 'confirmed');
                    const isDeposit = tx.type === 'deposit' || tx.direction === 'incoming';
                    const isTransfer = tx.type === 'transfer';
                    const statusLabel = isCancelled ? 'Cancelled' : isRejected ? 'Rejected' : isClaimed ? 'Claimed' : isActive ? 'Active' : isPending ? 'Pending Review' : isCompleted ? 'Completed' : 'Processing';
                    const statusColor = isCancelled ? 'text-slate-400' : isRejected ? 'text-red-400' : isClaimed ? 'text-purple-400' : isActive ? 'text-blue-400' : isPending ? 'text-amber-400' : isCompleted ? 'text-emerald-400' : 'text-amber-400';
                    return (
                      <div key={tx.id} className="flex items-center justify-between rounded-xl border border-slate-800/80 bg-black/10 px-4 py-3.5 transition-all hover:bg-black/20">
                        <div className="flex items-center gap-4">
                          <div className={`rounded-lg p-2 ${
                            tx.type === 'arbitrage_request' || tx.type === 'arbitrage_lock' ? 'bg-amber-500/10 text-amber-400' :
                            tx.type === 'arbitrage_payout' ? 'bg-emerald-500/10 text-emerald-400' :
                            tx.type === 'arbitrage_cancel_penalty' ? 'bg-red-500/10 text-red-400' :
                            tx.type === 'arbitrage_refund' ? 'bg-blue-500/10 text-blue-400' :
                            isTransfer ? 'bg-indigo-500/10 text-indigo-400' :
                            isDeposit ? 'bg-emerald-500/10 text-emerald-400' :
                            'bg-red-500/10 text-red-400'
                          }`}>
                            {tx.type === 'arbitrage_request' || tx.type === 'arbitrage_lock' ? <FaLock /> :
                             tx.type === 'arbitrage_payout' ? <FaArrowDown /> :
                             tx.type === 'arbitrage_cancel_penalty' ? <FaArrowUp /> :
                             tx.type === 'arbitrage_refund' ? <FaArrowDown /> :
                             isTransfer ? <FaExchangeAlt /> : isDeposit ? <FaArrowDown /> : <FaArrowUp />}
                          </div>
                          <div>
                            <p className="font-bold capitalize">{{
                              deposit: 'Deposit',
                              withdrawal: 'Withdrawal',
                              transfer: 'Transfer',
                              arbitrage_request: 'Arbitrage Request',
                              arbitrage_lock: 'Arbitrage Request',
                              arbitrage_refund: 'Arbitrage Refund',
                              arbitrage_payout: 'Arbitrage Payout',
                              arbitrage_cancel_penalty: 'Early Exit Fee'
                            }[tx.type] || tx.type}</p>
                            <p className="text-xs text-slate-500">{safeToDate(tx.timestamp)?.toLocaleString()}</p>
                            {tx.fromAccount && tx.toAccount && (
                              <p className="text-xs text-slate-600">{getAccountLabel(tx.fromAccount)} → {getAccountLabel(tx.toAccount)}</p>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold">{formatCrypto(tx.amount)} {tx.coin}</p>
                          <p className={`text-xs ${statusColor}`}>{statusLabel}</p>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="py-20 text-center text-slate-500">No transactions yet.</div>
                )}
              </div>
            </section>
          )}

          {activeSection === 'security' && (
            <section className="space-y-8">
              {activeSecuritySection === null ? (
                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {SECURITY_SECTIONS.map((section) => {
                    const Icon = section.icon;
                    return (
                      <button
                        key={section.key}
                        onClick={() => setActiveSecuritySection(section.key)}
                        className="group relative overflow-hidden rounded-3xl border border-white/10 bg-slate-900/60 p-8 text-left transition-all hover:-translate-y-1 hover:border-indigo-500/40"
                      >
                        <div className={`absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100 bg-gradient-to-br ${section.gradient} opacity-5`} />
                        <div className={`mb-4 inline-flex rounded-2xl bg-gradient-to-br ${section.gradient} p-4 shadow-lg`}>
                          <Icon className="h-6 w-6 text-white" />
                        </div>
                        <h3 className="text-xl font-bold text-white">{section.label}</h3>
                        <p className="mt-2 text-sm text-slate-400">{section.description}</p>
                        {section.key === 'verification' && (
                          <span className={`mt-3 inline-block rounded-full px-2.5 py-0.5 text-xs font-bold ${
                            userData?.verification_status === 'verified' ? 'bg-emerald-500/20 text-emerald-400'
                            : userData?.verification_status === 'pending' ? 'bg-amber-500/20 text-amber-400'
                            : userData?.verification_status === 'rejected' ? 'bg-red-500/20 text-red-400'
                            : 'bg-gray-500/20 text-gray-400'
                          }`}>
                            {userData?.verification_status === 'verified' ? 'Verified'
                              : userData?.verification_status === 'pending' ? 'Pending'
                              : userData?.verification_status === 'rejected' ? 'Rejected'
                              : 'Not Verified'}
                          </span>
                        )}
                        <div className="mt-6 flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-indigo-400 group-hover:text-indigo-300">
                          Manage <FaArrowRight />
                        </div>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="space-y-6">
                  <button
                    onClick={() => setActiveSecuritySection(null)}
                    className="flex items-center gap-2 text-sm font-bold text-slate-400 transition-colors hover:text-white"
                  >
                    <FaArrowLeft /> Back to Security Overview
                  </button>
                  
                  {activeSecuritySection === 'password' && (
                    <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-slate-900/90 to-gray-900/90 p-8 shadow-2xl backdrop-blur-xl">
                      <div className="mb-8 flex items-center gap-3">
                        <div className="rounded-xl bg-gradient-to-br from-rose-500 to-amber-500 p-2.5 shadow-lg">
                          <FaLock className="h-5 w-5 text-white" />
                        </div>
                        <h2 className="text-2xl font-bold">Password Controls</h2>
                      </div>
                      
                      <div className="grid gap-8 lg:grid-cols-2">
                        <div className="space-y-5 rounded-2xl bg-black/30 p-6 border border-slate-800/80">
                          <h3 className="font-bold">Update Password</h3>
                          <div className="space-y-4">
                            <input
                              type="password"
                              value={currentPasswordInput}
                              onChange={(e) => setCurrentPasswordInput(e.target.value)}
                              placeholder="Current Password"
                              className="w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-sm text-white"
                            />
                            <input
                              type="password"
                              value={newPasswordInput}
                              onChange={(e) => setNewPasswordInput(e.target.value)}
                              placeholder="New Password"
                              className="w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-sm text-white"
                            />
                            <button
                              onClick={handlePasswordChange}
                              disabled={isChangingPassword}
                              className="w-full rounded-xl bg-gradient-to-r from-rose-500 to-amber-500 py-3 font-bold uppercase tracking-wider text-white shadow-lg shadow-rose-500/20 disabled:opacity-50"
                            >
                              {isChangingPassword ? 'Updating...' : 'Update Password'}
                            </button>
                          </div>
                        </div>
                        <div className="space-y-5 rounded-2xl bg-black/30 p-6 border border-slate-800/80">
                          <h3 className="font-bold">Password Recovery</h3>
                          <div className="space-y-4">
                            <input
                              type="email"
                              value={resetEmailInput}
                              onChange={(e) => setResetEmailInput(e.target.value)}
                              placeholder="Recovery Email"
                              className="w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-sm text-white"
                            />
                            <button
                              onClick={handlePasswordResetLink}
                              disabled={isSendingPasswordReset}
                              className="w-full rounded-xl bg-gradient-to-r from-indigo-500 to-cyan-500 py-3 font-bold uppercase tracking-wider text-white shadow-lg shadow-indigo-500/20 disabled:opacity-50"
                            >
                              {isSendingPasswordReset ? 'Sending...' : 'Send Recovery Link'}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeSecuritySection === 'verification' && (
                    <div className="space-y-5">
                      {/* Header */}
                      <div className="rounded-2xl bg-gradient-to-r from-emerald-600/20 to-teal-600/20 border border-emerald-500/20 p-5 flex items-center gap-4">
                        <div className="rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 p-3 shadow-lg shrink-0">
                          <FaCheckCircle className="h-6 w-6 text-white" />
                        </div>
                        <div className="flex-1">
                          <h2 className="text-xl font-bold text-white">Identity Verification (KYC)</h2>
                          <p className="text-sm text-emerald-300/70 mt-0.5">Unlock full account access and higher limits</p>
                        </div>
                        <span className={`shrink-0 rounded-full px-3 py-1 text-xs font-bold border ${
                          userData?.verification_status === 'verified'
                            ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'
                            : userData?.verification_status === 'pending'
                            ? 'bg-amber-500/20 text-amber-400 border-amber-500/30'
                            : userData?.verification_status === 'rejected'
                            ? 'bg-red-500/20 text-red-400 border-red-500/30'
                            : 'bg-gray-500/20 text-gray-400 border-gray-500/30'
                        }`}>
                          {userData?.verification_status === 'verified' ? '✓ Verified'
                            : userData?.verification_status === 'pending' ? '⏳ Under Review'
                            : userData?.verification_status === 'rejected' ? '✗ Rejected'
                            : 'Not Verified'}
                        </span>
                      </div>

                      {userData?.verification_status === 'rejected' && userData?.verification_rejection_reason && (
                        <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400">
                          <strong>Reason:</strong> {userData.verification_rejection_reason}
                        </div>
                      )}

                      {userData?.verification_status === 'verified' && (
                        <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-5 text-center">
                          <FaCheckCircle className="mx-auto mb-2 h-10 w-10 text-emerald-400" />
                          <p className="font-bold text-emerald-400">Your account is fully verified</p>
                          <p className="text-xs text-gray-400 mt-1">You have access to all platform features and higher limits.</p>
                        </div>
                      )}

                      {userData?.verification_status === 'pending' && (
                        <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-5 text-center">
                          <div className="mx-auto mb-3 h-10 w-10 rounded-full border-4 border-amber-400/30 border-t-amber-400 animate-spin" />
                          <p className="font-bold text-amber-400">Verification Under Review</p>
                          <p className="text-xs text-gray-400 mt-1">Your documents are being processed through our secure verification system. This typically takes up to 24 hours.</p>
                        </div>
                      )}

                      {/* Upload Section */}
                      {userData?.verification_status !== 'verified' && userData?.verification_status !== 'pending' && (
                        <div className="space-y-5">
                          {/* Steps */}
                          <div className="grid grid-cols-3 gap-2 text-center text-xs">
                            {['Upload Document', 'System Review', 'Account Unlocked'].map((step, i) => (
                              <div key={i} className="flex flex-col items-center gap-1.5">
                                <div className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-bold border-2 ${i === 0 ? 'border-emerald-500 bg-emerald-500/20 text-emerald-400' : 'border-white/10 bg-white/5 text-gray-500'}`}>{i + 1}</div>
                                <span className={i === 0 ? 'text-emerald-400' : 'text-gray-500'}>{step}</span>
                              </div>
                            ))}
                          </div>

                          {/* Requirements */}
                          <div className="rounded-xl border border-white/10 bg-black/30 p-4 space-y-2">
                            <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3">Document Requirements</p>
                            {['Government-issued ID (Passport, National ID, or Driver\'s License)', 'Document must be valid and not expired', 'All four corners must be visible', 'Photo must be clear and readable'].map((req, i) => (
                              <div key={i} className="flex items-start gap-2 text-xs text-gray-400">
                                <span className="text-emerald-400 mt-0.5 shrink-0">✓</span>
                                <span>{req}</span>
                              </div>
                            ))}
                          </div>

                          {/* Upload Areas */}
                          <div className="grid gap-4 sm:grid-cols-2">
                            {/* Front */}
                            <div>
                              <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">Front Side</p>
                              <input type="file" accept="image/*" onChange={(e) => { const file = e.target.files[0]; if (file) { setVerificationFiles(prev => { const updated = [...prev]; updated[0] = { file, type: 'front', name: file.name }; return updated; }); } }} className="hidden" id="id-front-upload" />
                              <label htmlFor="id-front-upload" className={`flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed p-6 transition-all ${verificationFiles[0]?.file ? 'border-emerald-500/60 bg-emerald-500/10' : 'border-white/15 bg-black/20 hover:border-emerald-500/40 hover:bg-black/30'}`}>
                                {verificationFiles[0]?.file ? (
                                  <div className="text-center">
                                    <FaCheckCircle className="mx-auto mb-2 h-8 w-8 text-emerald-400" />
                                    <p className="text-xs font-semibold text-emerald-400 truncate max-w-[120px]">{verificationFiles[0].name}</p>
                                    <p className="text-xs text-gray-500 mt-1">Tap to replace</p>
                                  </div>
                                ) : (
                                  <div className="text-center">
                                    <div className="mx-auto mb-3 h-14 w-20 rounded-lg border-2 border-dashed border-white/20 flex items-center justify-center">
                                      <FaUpload className="h-5 w-5 text-gray-500" />
                                    </div>
                                    <p className="text-sm font-semibold text-gray-300">Upload Front</p>
                                    <p className="text-xs text-gray-500 mt-1">JPG, PNG · Max 5MB</p>
                                  </div>
                                )}
                              </label>
                            </div>

                            {/* Back */}
                            <div>
                              <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">Back Side</p>
                              <input type="file" accept="image/*" onChange={(e) => { const file = e.target.files[0]; if (file) { setVerificationFiles(prev => { const updated = [...prev]; updated[1] = { file, type: 'back', name: file.name }; return updated; }); } }} className="hidden" id="id-back-upload" />
                              <label htmlFor="id-back-upload" className={`flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed p-6 transition-all ${verificationFiles[1]?.file ? 'border-emerald-500/60 bg-emerald-500/10' : 'border-white/15 bg-black/20 hover:border-emerald-500/40 hover:bg-black/30'}`}>
                                {verificationFiles[1]?.file ? (
                                  <div className="text-center">
                                    <FaCheckCircle className="mx-auto mb-2 h-8 w-8 text-emerald-400" />
                                    <p className="text-xs font-semibold text-emerald-400 truncate max-w-[120px]">{verificationFiles[1].name}</p>
                                    <p className="text-xs text-gray-500 mt-1">Tap to replace</p>
                                  </div>
                                ) : (
                                  <div className="text-center">
                                    <div className="mx-auto mb-3 h-14 w-20 rounded-lg border-2 border-dashed border-white/20 flex items-center justify-center">
                                      <FaUpload className="h-5 w-5 text-gray-500" />
                                    </div>
                                    <p className="text-sm font-semibold text-gray-300">Upload Back</p>
                                    <p className="text-xs text-gray-500 mt-1">JPG, PNG · Max 5MB</p>
                                  </div>
                                )}
                              </label>
                            </div>
                          </div>

                          {/* Note */}
                          <div>
                            <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">Additional Note <span className="text-gray-600 normal-case font-normal">(optional)</span></p>
                            <textarea
                              value={verificationNote}
                              onChange={(e) => setVerificationNote(e.target.value)}
                              placeholder="e.g. document type, country of issue, or any other info..."
                              rows={2}
                              className="w-full rounded-xl border border-white/10 bg-black/30 px-4 py-3 text-sm text-white placeholder-gray-600 focus:border-emerald-500/40 focus:outline-none"
                            />
                          </div>

                          {/* Submit */}
                          <button
                            onClick={async () => {
                              if (verificationFiles.length === 0) {
                                showNotification({ type: 'error', message: 'Please select at least one document' });
                                return;
                              }
                              try {
                                const userDoc = await getDoc(doc(db, 'userProfiles', currentUser.uid));
                                const currentData = userDoc.data() || {};
                                const submissionCount = (currentData.verification_submission_count || 0) + 1;
                                await updateDoc(doc(db, 'userProfiles', currentUser.uid), {
                                  verification_status: 'pending',
                                  verification_submission_count: submissionCount,
                                  verification_requested_at: serverTimestamp(),
                                  verification_note: verificationNote || '',
                                  last_submitted_at: serverTimestamp()
                                });
                                setVerificationFiles([]);
                                setVerificationNote('');
                                showNotification({ type: 'success', message: 'Your documents have been submitted to our secure verification system. You\'ll be notified once verified.' });
                              } catch (error) {
                                console.error('Error submitting verification:', error);
                                showNotification({ type: 'error', message: 'Failed to submit verification. Please try again.' });
                              }
                            }}
                            disabled={verificationFiles.length === 0}
                            className="w-full rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 py-4 text-sm font-bold uppercase tracking-widest text-white shadow-lg shadow-emerald-500/20 transition-all hover:shadow-emerald-500/40 disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            Submit for Verification
                          </button>

                          <div className="flex items-center justify-center gap-2 text-xs text-gray-600">
                            <FaLock className="h-3 w-3" />
                            <span>Your data is encrypted and processed securely. We do not share your information with third parties.</span>
                          </div>
                        </div>
                      )}

                      {userData?.verification_status === 'verified' && (
                        <div className="rounded-2xl bg-emerald-500/10 border border-emerald-500/20 p-6 text-center">
                          <FaCheckCircle className="mx-auto mb-3 h-12 w-12 text-emerald-400" />
                          <h3 className="text-lg font-bold text-emerald-400">Account Verified</h3>
                          <p className="text-sm text-gray-400 mt-1">Your identity has been verified successfully</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Trusted Contact Channels */}
                  {activeSecuritySection === 'contacts' && (
                    <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-slate-900/90 to-gray-900/90 p-8 shadow-2xl backdrop-blur-xl">
                      <div className="mb-8 flex items-center gap-3">
                        <div className="rounded-xl bg-gradient-to-br from-sky-500 to-cyan-500 p-2.5 shadow-lg">
                          <FaTelegramPlane className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <h2 className="text-2xl font-bold">Trusted Contact Channels</h2>
                          <p className="text-sm text-gray-400">Configure Telegram or WhatsApp alerts for security events</p>
                        </div>
                      </div>

                      {/* Channel Selection */}
                      <div className="mb-6">
                        <label className="mb-3 block text-sm font-bold text-gray-300">Select Channel</label>
                        <div className="flex gap-4">
                          <button
                            onClick={() => setContactMethod('telegram')}
                            className={`flex items-center gap-2 rounded-xl border px-4 py-3 transition-all ${
                              contactMethod === 'telegram'
                                ? 'border-sky-500 bg-sky-500/20 text-sky-400'
                                : 'border-white/10 bg-black/40 text-gray-400 hover:border-white/20'
                            }`}
                          >
                            <FaTelegramPlane className="text-xl" />
                            Telegram
                          </button>
                          <button
                            onClick={() => setContactMethod('whatsapp')}
                            className={`flex items-center gap-2 rounded-xl border px-4 py-3 transition-all ${
                              contactMethod === 'whatsapp'
                                ? 'border-emerald-500 bg-emerald-500/20 text-emerald-400'
                                : 'border-white/10 bg-black/40 text-gray-400 hover:border-white/20'
                            }`}
                          >
                            <FaWhatsapp className="text-xl" />
                            WhatsApp
                          </button>
                        </div>
                      </div>

                      {/* Phone Number Input */}
                      {contactAction === 'request' && (
                        <div className="space-y-4">
                          <div>
                            <label className="mb-2 block text-sm font-bold text-gray-300">
                              {contactMethod === 'telegram' ? 'Telegram Username' : 'WhatsApp Number'}
                            </label>
                            <input
                              type="text"
                              value={contactNumber}
                              onChange={(e) => setContactNumber(e.target.value)}
                              placeholder={contactMethod === 'telegram' ? '@username' : '+1234567890'}
                              className="w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-white placeholder-gray-500"
                            />
                          </div>
                          <button
                            onClick={handleSendContactCode}
                            disabled={isSendingCode || !contactNumber}
                            className="w-full rounded-xl bg-gradient-to-r from-sky-500 to-cyan-500 py-3 font-bold uppercase tracking-wider text-white shadow-lg shadow-sky-500/20 disabled:opacity-50"
                          >
                            {isSendingCode ? 'Sending...' : 'Get Code'}
                          </button>
                        </div>
                      )}

                      {/* Verification Code Input */}
                      {contactAction === 'submit' && (
                        <div className="space-y-4">
                          <div>
                            <label className="mb-2 block text-sm font-bold text-gray-300">
                              Enter Verification Code ({contactMethod === 'telegram' ? '5 digits' : '6 digits'})
                            </label>
                            <input
                              type="text"
                              value={verificationCodeInput}
                              onChange={(e) => setVerificationCodeInput(e.target.value)}
                              placeholder={contactMethod === 'telegram' ? '5-digit code' : '6-digit code'}
                              maxLength={contactMethod === 'whatsapp' ? 6 : 5}
                              className="w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-white placeholder-gray-500"
                            />
                          </div>
                          <button
                            onClick={handleVerifyContactCode}
                            disabled={isVerifyingCode || !verificationCodeInput}
                            className="w-full rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 py-3 font-bold uppercase tracking-wider text-white shadow-lg shadow-emerald-500/20 disabled:opacity-50"
                          >
                            {isVerifyingCode ? 'Verifying...' : 'Submit Code'}
                          </button>
                          <button
                            onClick={() => setContactAction('request')}
                            className="w-full text-sm text-gray-400 hover:text-white"
                          >
                            Change phone number
                          </button>
                        </div>
                      )}

                      {/* Current Status */}
                      {contactMethodsData?.[contactMethod]?.status && (
                        <div className="mt-6 rounded-2xl border border-white/10 bg-black/30 p-4">
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-400">Current Status:</span>
                            <span className={`rounded-full px-3 py-1 text-xs font-bold ${
                              contactMethodsData[contactMethod].status === 'verified'
                                ? 'bg-green-500/20 text-green-400'
                                : contactMethodsData[contactMethod].status === 'code_requested' || contactMethodsData[contactMethod].status === 'code_submitted'
                                ? 'bg-yellow-500/20 text-yellow-400'
                                : 'bg-gray-500/20 text-gray-400'
                            }`}>
                              {formatContactStatus(contactMethodsData[contactMethod].status)}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Customer Service */}
                  {activeSecuritySection === 'support' && (
                    <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-slate-900/90 to-gray-900/90 p-8 shadow-2xl backdrop-blur-xl">
                      <div className="mb-8 flex items-center gap-3">
                        <div className="rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 p-2.5 shadow-lg">
                          <FaHeadset className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <h2 className="text-2xl font-bold">Customer Service</h2>
                          <p className="text-sm text-gray-400">Chat with Gemini AI support</p>
                        </div>
                      </div>

                      <div className="flex flex-col gap-4">
                        {supportContacts.telegram && (
                          <a
                            href={supportContacts.telegram}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-4 rounded-xl border border-white/10 bg-black/40 p-4 transition-all hover:border-sky-500/50 hover:bg-sky-500/10"
                          >
                            <FaTelegramPlane className="h-8 w-8 text-sky-400" />
                            <div>
                              <h3 className="font-bold text-white">Telegram Support</h3>
                              <p className="text-sm text-gray-400">Click to open chat</p>
                            </div>
                            <FaArrowRight className="ml-auto text-gray-400" />
                          </a>
                        )}
                        <a
                          href="mailto:geminiconcierge.net@gmail.com"
                          className="flex items-center gap-4 rounded-xl border border-white/10 bg-black/40 p-4 transition-all hover:border-blue-500/50 hover:bg-blue-500/10"
                        >
                          <FaEnvelope className="h-8 w-8 text-blue-400" />
                          <div>
                            <h3 className="font-bold text-white">Email Support</h3>
                            <p className="text-sm text-gray-400">geminiconcierge.net@gmail.com</p>
                          </div>
                          <FaArrowRight className="ml-auto text-gray-400" />
                        </a>
                        {!supportContacts.telegram && !supportContacts.whatsapp && (
                          <p className="text-center text-gray-400">Reach us at geminiconcierge.net@gmail.com or Telegram @GeminiAI_Support_Com</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;
